import { ColumnDef, CellContext } from '@tanstack/react-table';
import { DataTableColumn, DataTableRow, ColumnMeta } from '../types';

/**
 * Convert DataTableColumn to TanStack ColumnDef
 *
 * This function transforms our simplified column format to TanStack's ColumnDef format.
 * TanStack requires either accessorKey for direct data access or a custom cell renderer.
 */
export function convertToTanstackColumns<T extends DataTableRow>(columns: DataTableColumn<T>[]): ColumnDef<T>[] {
  return columns.map((col): ColumnDef<T> => {
    const meta: ColumnMeta = {
      width: col.width,
      sortFieldId: col.sortFieldId
    };

    // If column has accessorKey, create accessor column
    if (col.accessorKey) {
      return {
        id: col.id,
        header: col.header,
        accessorKey: col.accessorKey as string,
        cell: col.cell ? (context: CellContext<T, unknown>) => col.cell?.(context.row.original) : undefined,
        enableSorting: col.enableSorting,
        enableHiding: col.enableHiding,
        meta
      } as ColumnDef<T>;
    }

    // If column has custom cell renderer without accessorKey, create display column
    if (col.cell) {
      return {
        id: col.id,
        header: col.header,
        cell: (context: CellContext<T, unknown>) => col.cell?.(context.row.original),
        enableSorting: col.enableSorting ?? false,
        enableHiding: col.enableHiding,
        meta
      } as ColumnDef<T>;
    }

    // Fallback: display column without data
    return {
      id: col.id,
      header: col.header,
      cell: () => null,
      enableSorting: false,
      enableHiding: col.enableHiding,
      meta
    } as ColumnDef<T>;
  });
}
